import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { FORM_ERROR, type FormApi } from "final-form";
import { Form } from "react-final-form";
import { BiLock } from "react-icons/bi";
import NoDataFound from "../../../../../components/common/NoDataFound/NoDataFound";
import type {
  ChannelCatalogItem,
  ConnectionHubCloseReason,
  ConnectionHubError,
  DraftView,
  EmailSuggestions,
  SetupResponse,
} from "../../../api/connectionHub.types";
import { CONNECTION_HUB_CLOSE_REASON } from "../../../api/connectionHub.types";
import { connectionHubApi } from "../../../api/connectionHub.api";
import { useDraftAutosave } from "../../../hooks/useDraftAutosave";
import { isConnectionHubError } from "../../../utils/handleConnectionError";
import ChannelCredentialCard from "../../providers/ChannelCredentialCard/ChannelCredentialCard";
import ConnectionErrorPanel from "../../common/ConnectionErrorPanel/ConnectionErrorPanel";
import InfoBanner from "../../common/InfoBanner/InfoBanner";
import ConnectModalShell from "../ConnectModalShell/ConnectModalShell";
import type { CredentialChannelModel, CredentialFormValues, EditingChannel } from "./CredentialsStep.types";
import {
  initialCredentialValue,
  mergeEmailSuggestions,
  toConnectionInput,
  validateCredentials,
  type CredentialFormErrors,
} from "./credentials.utils";
import styles from "./CredentialsStep.module.scss";

export interface CredentialsStepProps {
  launchId: number;
  visible: boolean;
  requestedIpIds: string[];
  channels: ChannelCatalogItem[];
  drafts: DraftView[];
  emailSuggestions: EmailSuggestions | null;
  agencyClientId: number | null;
  /**
   * Edit mode. These channels already exist, so their email is locked, their card cannot be
   * deselected and nothing is autosaved as a draft — there is a real connection behind each one.
   */
  editing?: EditingChannel[];
  onBack?: () => void;
  onClose: (reason: ConnectionHubCloseReason) => void;
  onSetupComplete: (setup: SetupResponse) => void;
  onSelectionChange: (ipIds: string[]) => void;
  registerFlush?: (flush: (() => Promise<void>) | null) => void;
}

interface SetupFieldError { ipId?: unknown; field?: unknown; message?: unknown; }

const setupSubmissionErrors = (error: ConnectionHubError, models: CredentialChannelModel[]): Record<string, unknown> => {
  const details = Array.isArray(error.details) ? error.details as SetupFieldError[] : [];
  const channels: Array<Record<string, string> | undefined> = [];
  details.forEach((detail) => {
    if (typeof detail.ipId !== "string" || typeof detail.field !== "string" || typeof detail.message !== "string") return;
    const index = models.findIndex((model) => model.channel.ipId === detail.ipId);
    if (index < 0) return;
    channels[index] = { ...(channels[index] ?? {}), [detail.field]: detail.message };
  });
  return channels.some(Boolean) ? { channels } : { [FORM_ERROR]: error.message };
};

interface FormShellProps extends Omit<CredentialsStepProps, "channels" | "drafts" | "requestedIpIds"> {
  models: CredentialChannelModel[];
  selectedIpIds: string[];
  setSelectedIpIds: Dispatch<SetStateAction<string[]>>;
  form: FormApi<CredentialFormValues>;
  values: CredentialFormValues;
  invalid: boolean;
  submitting: boolean;
  submitError?: string;
  submit: () => Promise<unknown>;
}

const CredentialsFormShell = ({ launchId, visible, models, selectedIpIds, setSelectedIpIds, form, values, invalid, submitting, submitError, submit, emailSuggestions, agencyClientId, editing, onBack, onClose, onSelectionChange, registerFlush }: FormShellProps): JSX.Element => {
  const [continuing, setContinuing] = useState(false);
  const editMode = Boolean(editing?.length);
  const modelByIpId = useMemo(() => new Map(models.map((model) => [model.channel.ipId, model])), [models]);
  const selectedInputs = useMemo(() => selectedIpIds.flatMap((ipId) => {
    const model = modelByIpId.get(ipId);
    const value = model ? values.channels[model.formIndex] : undefined;
    return model && value ? [toConnectionInput(value, model.channel.capability)] : [];
  }), [modelByIpId, selectedIpIds, values.channels]);
  // An existing connection has nothing to draft, and a draft row would shadow it on the next resolve.
  const autosaveInputs = useMemo(() => (editMode ? [] : selectedInputs), [editMode, selectedInputs]);

  const handleSaved = useCallback((savedDrafts: DraftView[]): void => {
    const passwordByIpId = new Map(savedDrafts.map((draft) => [draft.ipId, draft.hasPassword]));
    const current = form.getState().values.channels;
    form.change("channels", current.map((value) => passwordByIpId.has(value.ipId)
      ? { ...value, hasStoredPassword: passwordByIpId.get(value.ipId) ?? false }
      : value));
  }, [form]);
  const autosave = useDraftAutosave({ launchId, agencyClientId, inputs: autosaveInputs, onSaved: handleSaved });
  const suggestions = useMemo(() => mergeEmailSuggestions(emailSuggestions, values.channels), [emailSuggestions, values.channels]);

  const requestClose = useCallback(async (reason: ConnectionHubCloseReason): Promise<void> => {
    await autosave.flush();
    onClose(reason);
  }, [autosave, onClose]);

  useEffect(() => {
    registerFlush?.(autosave.flush);
    return () => registerFlush?.(null);
  }, [autosave.flush, registerFlush]);

  // The card goes as soon as it is clicked; the draft PUT follows in the background and only the
  // removed card is restored if it fails, so the rest of the form never freezes.
  const remove = useCallback((model: CredentialChannelModel): void => {
    const { ipId } = model.channel;
    const value = values.channels[model.formIndex];
    if (!value || !selectedIpIds.includes(ipId)) return;
    const next = selectedIpIds.filter((item) => item !== ipId);
    const input = toConnectionInput(value, model.channel.capability);

    const currentChannels = form.getState().values.channels;
    form.change("channels", currentChannels.map((channelValue, index) => index === model.formIndex
      ? { ...channelValue, password: "", passwordEdited: false }
      : channelValue));
    setSelectedIpIds(next);
    onSelectionChange(next);

    void (async () => {
      const saved = await autosave.flushCard(input);
      if (saved) {
        if (!next.length) onClose(CONNECTION_HUB_CLOSE_REASON.PROGRAMMATIC);
        return;
      }
      setSelectedIpIds((current: string[]) => (current.includes(ipId) ? current : [...current, ipId]));
      onSelectionChange([...next, ipId]);
    })();
  }, [autosave, form, onClose, onSelectionChange, selectedIpIds, setSelectedIpIds, values.channels]);

  return (
    <ConnectModalShell
      visible={visible}
      title="Enter Credentials"
      bodyClassName={styles.credentialsBody}
      subHeader={<InfoBanner icon={<BiLock />}>Credentials are only used to connect the channels.</InfoBanner>}
      primaryLabel="Continue"
      {...(onBack ? { onBack } : {})}
      primaryLoading={continuing || submitting}
      primaryDisabled={!selectedIpIds.length || invalid || continuing || submitting}
      onPrimary={() => {
        void (async () => {
          if (continuing) return;
          setContinuing(true);
          try {
            await autosave.flush();
            await submit();
          } finally {
            setContinuing(false);
          }
        })();
      }}
      onClose={() => { void requestClose(CONNECTION_HUB_CLOSE_REASON.CLOSE_BUTTON); }}
      onDismiss={(reason) => { void requestClose(reason === "escape" ? CONNECTION_HUB_CLOSE_REASON.ESCAPE : CONNECTION_HUB_CLOSE_REASON.BACKDROP); }}
    >
      <div className={styles.root} aria-busy={continuing || submitting}>
        {/* The form-level submit error had no home before, so setup failures were toast-only. */}
        <ConnectionErrorPanel error={submitError} />
        {!models.length ? (
          <div className={styles.emptyState}>
            <NoDataFound title="Marketplace details are unavailable" subTitle="Close this window and try again." />
          </div>
        ) : (
          <div className={styles.cards}>
            {models.filter((model) => selectedIpIds.includes(model.channel.ipId)).map((model) => (
              <ChannelCredentialCard key={model.channel.ipId} model={model} value={values.channels[model.formIndex]} suggestions={suggestions} disabled={submitting} lockEmail={editMode} removable={!editMode} onRemove={() => { void remove(model); }} />
            ))}
          </div>
        )}
      </div>
    </ConnectModalShell>
  );
};

export const CredentialsStep = (props: CredentialsStepProps): JSX.Element => {
  const draftsByIpId = useMemo(() => new Map(props.drafts.map((draft) => [draft.ipId, draft])), [props.drafts]);
  const models = useMemo<CredentialChannelModel[]>(() => {
    // A resolve that reopens a saved draft returns it in `drafts` without repeating
    // the ipId in `channelsNeedingCredentials`, so both are row sources.
    const requested = new Set([...props.requestedIpIds, ...props.drafts.map((draft) => draft.ipId)]);
    return props.channels.filter((channel) => requested.has(channel.ipId)).sort((left, right) => left.capability.fillOrder - right.capability.fillOrder || left.ipName.localeCompare(right.ipName)).map((channel, formIndex) => ({ channel, draft: draftsByIpId.get(channel.ipId), formIndex }));
  }, [draftsByIpId, props.channels, props.drafts, props.requestedIpIds]);
  const [selectedIpIds, setSelectedIpIds] = useState(() => models.map((model) => model.channel.ipId));
  const capabilities = useMemo(() => new Map(models.map((model) => [model.channel.ipId, model.channel.capability])), [models]);
  const editingByIpId = useMemo(() => new Map((props.editing ?? []).map((item) => [item.ipId, item])), [props.editing]);
  const initialValues = useMemo<CredentialFormValues>(
    () => ({
      channels: models.map((model) =>
        initialCredentialValue(model.channel.ipId, model.channel.capability, model.draft, editingByIpId.get(model.channel.ipId))
      ),
    }),
    [editingByIpId, models]
  );

  useEffect(() => {
    const valid = new Set(models.map((model) => model.channel.ipId));
    setSelectedIpIds((current) => {
      const next = current.filter((ipId) => valid.has(ipId));
      return next.length === current.length ? current : next;
    });
  }, [models]);

  return (
    <Form<CredentialFormValues>
      initialValues={initialValues}
      initialValuesEqual={(left, right) => JSON.stringify(left) === JSON.stringify(right)}
      validate={(values) => validateCredentials(values, selectedIpIds, capabilities) as CredentialFormErrors}
      onSubmit={async (values) => {
        const selected = new Set(selectedIpIds);
        const channels = models.flatMap((model) => selected.has(model.channel.ipId) ? [toConnectionInput(values.channels[model.formIndex], model.channel.capability)] : []);
        try {
          const setup = await connectionHubApi.setup({ channels, agencyClientId: props.agencyClientId });
          props.onSetupComplete(setup);
          return undefined;
        } catch (caught) {
          if (isConnectionHubError(caught)) return setupSubmissionErrors(caught, models);
          return { [FORM_ERROR]: "Something went wrong. Please try again." };
        }
      }}
    >
      {({ form, values, invalid, submitting, submitError, handleSubmit }) => (
        <CredentialsFormShell {...props} models={models} selectedIpIds={selectedIpIds} setSelectedIpIds={setSelectedIpIds} form={form} values={values} invalid={invalid} submitting={submitting} submitError={submitError} submit={handleSubmit} />
      )}
    </Form>
  );
};

export default CredentialsStep;
